import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquare, LayoutDashboard, FolderKanban, Compass, Megaphone, PiggyBank,
  LayoutGrid, BarChart3, Settings, LogOut, Sparkles, Plus, Network
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { LOGO_URL } from "../../data/landing";
import SidebarItem from "./SidebarItem";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator
} from "../ui/dropdown-menu";

const NAV = [
  { to: "/workspace", label: "AI Workspace", icon: MessageSquare, end: true },
  { to: "/workspace/dashboard", label: "BI Dashboard", icon: LayoutDashboard },
  { to: "/workspace/projects", label: "Projects", icon: FolderKanban },
  { to: "/workspace/strategy-marketing", label: "Strategy & Marketing", icon: Compass },
  { to: "/workspace/funding", label: "Funding", icon: PiggyBank },
  { to: "/workspace/templates", label: "Templates", icon: LayoutGrid },
  { to: "/workspace/network-connector", label: "Network Connector", icon: Network },
  { to: "/workspace/settings", label: "Settings", icon: Settings },
];

export default function Sidebar({ collapsed, mobileOpen, onClose, onNewChat }) {
  const { user, logout, subscription } = useAuth();
  const navigate = useNavigate();

  // Sidebar transition styling
  const sidebarStyle = {
    transition: "width 0.28s ease, transform 0.28s ease",
  };

  const logoStyle = {
    transition: "all 0.28s ease",
  };

  return (
    <>
      {/* Mobile drawer overlay backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-xs z-40 lg:hidden transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      <aside
        style={sidebarStyle}
        className={`fixed top-0 bottom-0 left-0 bg-white border-r border-gray-100 flex flex-col z-45 ${
          // On mobile, sidebar is a fixed 260px drawer sliding from left
          mobileOpen ? "translate-x-0 w-[260px]" : "-translate-x-full lg:translate-x-0"
        } ${
          // On desktop, width transitions between 260px (expanded) and 72px (collapsed)
          collapsed ? "lg:w-[72px]" : "lg:w-[260px]"
        }`}
      >
        {/* Logo & Application Name */}
        <Link
          to="/"
          onClick={() => {
            if (onClose) onClose();
          }}
          className={`h-16 w-full max-w-full overflow-hidden box-border flex items-center border-b border-gray-100 focus:outline-none hover:bg-gray-50/50 transition-all duration-300 ${
            collapsed && !mobileOpen ? "px-2.5 justify-center" : "px-5 justify-start"
          }`}
          data-testid="workspace-home"
          style={logoStyle}
          title={collapsed && !mobileOpen ? "STUDLYF Home" : undefined}
        >
          <div className="flex items-center justify-center w-full max-w-full overflow-hidden shrink-0">
            <img
              src={LOGO_URL}
              alt="STUDLYF"
              className={`object-contain transition-all duration-300 shrink-0 ${
                collapsed && !mobileOpen
                  ? "h-7 max-w-[48px]"
                  : "h-10 md:h-11 max-w-[160px]"
              }`}
            />
          </div>
        </Link>

        {/* New Chat Button */}
        <div className={`p-3 border-b border-gray-50 ${collapsed ? "lg:flex lg:justify-center" : ""}`}>
          <button
            onClick={onNewChat}
            className={`flex items-center text-gray-700 font-medium text-sm transition-all duration-200 bg-white border border-gray-200 hover:border-[#7C3AED]/40 hover:bg-gray-50 hover:scale-[1.03] shadow-xs shrink-0 ${
              collapsed
                ? "w-10 h-10 rounded-xl justify-center"
                : "w-full gap-2.5 px-4 py-2.5 rounded-xl justify-start"
            }`}
            data-testid="sidebar-new-chat"
            title={collapsed ? "New chat" : undefined}
          >
            <Plus className="w-4 h-4 text-[#7C3AED] shrink-0" />
            {(!collapsed || mobileOpen) && (
              <span className="animate-in fade-in-0 duration-200 whitespace-nowrap">New chat</span>
            )}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto no-scrollbar">
          {NAV.map((it) => (
            <SidebarItem
              key={it.to}
              to={it.to}
              label={it.label}
              icon={it.icon}
              end={it.end}
              collapsed={collapsed && !mobileOpen}
            />
          ))}
        </nav>

        {/* Pro Trial & User profile section */}
        <div className="border-t border-gray-100 p-3 bg-white">
          {/* Pro Trial / Upgrade dynamic box */}
          {(!collapsed || mobileOpen) && (
            subscription?.plan_name === "STUDLYF Pro" && subscription?.status === "active" ? (
              <div className="rounded-2xl p-3 bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white relative overflow-hidden mb-3 animate-in fade-in-0 duration-200">
                <Sparkles className="w-4 h-4" />
                <p className="mt-2 text-sm font-semibold leading-tight">STUDLYF Pro Active</p>
                <p className="text-[11px] opacity-90 mt-0.5">{subscription.remaining_days} days left</p>
              </div>
            ) : (
              <div className="rounded-2xl p-3 bg-gray-50 border border-gray-150 text-gray-700 relative overflow-hidden mb-3 animate-in fade-in-0 duration-200">
                <p className="text-xs font-semibold leading-tight text-gray-900">Upgrade to STUDLYF Pro</p>
                <p className="text-[10px] text-gray-500 mt-1">Unlock templates, GTM strategy & advanced analytics.</p>
                <button
                  onClick={() => navigate("/workspace/settings?tab=billing")}
                  className="mt-3 w-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white hover:opacity-90 transition-all rounded-xl py-2 text-center text-xs font-bold shadow-xs flex items-center justify-center gap-1"
                >
                  Upgrade to Pro
                </button>
              </div>
            )
          )}

          {/* User profile details */}
          {user && (
            <div className={`flex items-center ${collapsed && !mobileOpen ? "justify-center" : "gap-2.5"}`}>
              {collapsed && !mobileOpen ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="focus:outline-none hover:scale-[1.03] transition-transform duration-200" aria-label="User menu">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover border border-gray-100" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center font-semibold text-sm">
                          {(user?.name || user?.email || "").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-2xl ml-3 w-56">
                    <DropdownMenuLabel>
                      <p className="text-sm font-semibold truncate">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate font-normal">{user.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                      data-testid="workspace-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center font-semibold text-sm shrink-0">
                      {(user?.name || user?.email || "").slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" data-testid="workspace-username">{user.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={logout}
                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900"
                    data-testid="workspace-logout"
                    aria-label="Logout"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
